// Ported 1:1 from ~/Desktop/rondo/src/components/charts/ChartRangeToggle.tsx.

import { useId, type ReactNode } from 'react'
import { motion } from 'motion/react'

interface ChartRangeToggleProps<T extends string | number = string> {
  range: T
  onRangeChange: (r: T) => void
  options?: (T | { value: T; label: string })[]
  renderLabel?: (item: { value: T; label: string }, isActive: boolean) => ReactNode
  stretch?: boolean
  size?: 'sm' | 'lg'
  indicatorClassName?: string
  tone?: 'neutral' | 'on-brand'
  textSize?: 'label' | 'body'
}

export function ChartRangeToggle<T extends string | number = string>({
  range,
  onRangeChange,
  options,
  renderLabel,
  stretch,
  size = 'sm',
  indicatorClassName,
  tone = 'neutral',
  textSize = 'label',
}: ChartRangeToggleProps<T>) {
  const id = useId()
  const items = (options ?? (['24h', '7d'] as T[])).map(
    (o): { value: T; label: string } =>
      typeof o === 'string' || typeof o === 'number'
        ? { value: o as T, label: String(o) }
        : o,
  )

  const wrapperPad = size === 'lg' ? 'p-1.5' : 'p-1'
  const wrapperText =
    textSize === 'body' ? 'text-body' : size === 'lg' ? 'text-body' : 'text-label'
  const buttonPad = size === 'lg' ? 'px-5 py-3' : 'px-4 py-1.5'
  const indicatorClass = indicatorClassName ?? 'bg-[#000000]'
  const inactiveText =
    tone === 'on-brand'
      ? 'text-black/55 hover:text-black/80 active:text-black'
      : 'text-grey hover:text-dark-grey active:text-black'
  const inactiveSpan = tone === 'on-brand' ? 'text-black/55' : 'text-grey'

  return (
    <div className={`flex rounded-full bg-black/10 ${wrapperPad} ${wrapperText}`}>
      {items.map((item) => {
        const isActive = range === item.value
        return (
          <button
            key={String(item.value)}
            onClick={() => onRangeChange(item.value)}
            className={`relative ${buttonPad} rounded-full transition-colors ${
              stretch ? 'flex-1' : ''
            } ${!isActive ? inactiveText : ''}`}
          >
            {isActive && (
              <motion.div
                layoutId={`range-indicator-${id}`}
                className={`absolute inset-0 ${indicatorClass} rounded-full shadow-small`}
              />
            )}
            {renderLabel ? (
              renderLabel(item, isActive)
            ) : (
              <span
                className={`relative z-10 ${isActive ? 'text-[#FFFFFF]' : inactiveSpan}`}
              >
                {item.label}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
